import React from 'react';
import { useForm } from 'react-hook-form';
// import { zodResolver } from '@hookform/resolvers';
import { z } from 'zod';
import { X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { AuthUser, CreateUserForm, UpdateUserForm } from '../../types';

const createUserSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  phoneNumber: z.string().min(1, 'Phone number is required'),
  role: z.enum(['user', 'admin', 'super_admin', 'client']),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

const updateUserSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  phoneNumber: z.string().min(1, 'Phone number is required'),
  role: z.enum(['user', 'admin', 'super_admin', 'client']),
  isActive: z.boolean(),
});

type CreateUserFormData = z.infer<typeof createUserSchema>;
type UpdateUserFormData = z.infer<typeof updateUserSchema>;

interface UserFormProps {
  user?: AuthUser;
  onSubmit: (data: CreateUserForm | UpdateUserForm) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export const UserForm: React.FC<UserFormProps> = ({
  user,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const isEditing = !!user;
  // const schema = isEditing ? updateUserSchema : createUserSchema;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateUserFormData | UpdateUserFormData>({
    // resolver: zodResolver(schema, { resolver: 'zod' }),
    defaultValues: user
      ? {
          firstName: user.firstName,
          lastName: user.lastName,
          phoneNumber: user.phoneNumber,
          role: user.role,
          isActive: user.isActive,
        }
      : {
          firstName: '',
          lastName: '',
          email: '',
          phoneNumber: '',
          role: 'user',
          password: '',
        },
  });

  const handleFormSubmit = async (data: CreateUserFormData | UpdateUserFormData) => {
    await onSubmit(data as CreateUserForm | UpdateUserForm);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{isEditing ? 'Edit User' : 'Create User'}</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              leftIcon={<X className="h-4 w-4" />}
            />
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="First Name"
                {...register('firstName')}
                error={errors.firstName?.message}
                placeholder="Enter first name"
              />
              <Input
                label="Last Name"
                {...register('lastName')}
                error={errors.lastName?.message}
                placeholder="Enter last name"
              />
            </div>

            {!isEditing && (
              <Input
                label="Email"
                type="email"
                {...register('email')}
                error={(errors as any).email?.message}
                placeholder="Enter email address"
              />
            )}

            <Input
              label="Phone Number"
              {...register('phoneNumber')}
              error={errors.phoneNumber?.message}
              placeholder="Enter phone number"
            />

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Role
              </label>
              <select
                {...register('role')}
                className="block w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
                <option value="client">Client</option>
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-danger-600">{errors.role.message}</p>
              )}
            </div>

            {!isEditing && (
              <Input
                label="Password"
                type="password"
                {...register('password')}
                error={(errors as any).password?.message}
                placeholder="Enter password"
              />
            )}

            {isEditing && (
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    {...register('isActive')}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-secondary-700">Active</span>
                </label>
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <Button
                type="submit"
                className="flex-1"
                loading={loading}
                disabled={loading}
              >
                {isEditing ? 'Update User' : 'Create User'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onCancel}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
